import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useAccountStatements from 'features/myVaro/hooks/statementsAndForms/useAccountStatements';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';

const AccountYearSelectionScreen = () => {
  const router = useRouter();
  const {
    setMonthSelectionScreenTitle,
    yearSelectionScreenTitle,
    setYearSelectionScreenTitle,
  } = useContext(MyVaroContext);
  const flow = useVaroUserFlow();

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'statementsAndForms',
  });

  const {
    id,
    category = '',
    account_type = '',
  } = useLocalSearchParams<{
    id: string;
    category: string;
    account_type: string;
  }>();

  const {
    yearlyStatementsData,
    accountStatementsDetails,
    loading,
    errorModel,
  } = useAccountStatements({
    accountId: id,
  });

  const onYearPress = (year: string) => {
    router.push({
      pathname: `/${flow}/statements-and-forms/account-statements/[id]/[year]`,
      params: {
        id,
        year,
        category,
        account_type,
      },
    });
  };

  //Handles Screen title if we hit the url directly or get redirected from another screen
  useEffect(() => {
    accountStatementsDetails?.forEach((account) => {
      if (account.id === id) {
        setYearSelectionScreenTitle?.(
          yearlyStatementsData?.length
            ? account.name
            : translatedText('accountStatements.title'),
        );
      }
    });
  }, [
    accountStatementsDetails,
    id,
    setYearSelectionScreenTitle,
    yearlyStatementsData?.length,
    translatedText,
  ]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: yearSelectionScreenTitle,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {yearlyStatementsData?.length ? (
        <FlatList
          data={yearlyStatementsData}
          renderItem={({ item }) => (
            <ListItem
              key={item?.id}
              primaryCopy={item?.title}
              onPress={() => {
                setMonthSelectionScreenTitle?.(item?.title);
                onYearPress(item?.year?.toString() ?? '');
              }}
              analyticsId="year-pressed"
              customAttributes={{
                account_type,
                category,
                cta: item?.title,
              }}
            />
          )}
          ItemSeparatorComponent={() => (
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
          )}
          keyExtractor={(item) => item?.id}
          removeClippedSubviews={false}
        />
      ) : (
        <View style={styles.emptyStateContainer}>
          {!loading ? (
            <EmptyStateScreen
              headlineText={translatedText('accountStatements.title')}
              illustrationKey="BANK_A"
              bodyText={translatedText('accountStatements.emptyStateBodyText')}
            />
          ) : null}
        </View>
      )}
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { backgroundColor: theme.color.background.primary, flexGrow: 1 },
  emptyStateContainer: { flexGrow: 1 },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
});

export default AccountYearSelectionScreen;
